import React, { useEffect, useState, useCallback } from "react";
import {
  Alert, Button, Card, Col, DatePicker, Descriptions, Empty, Form,
  Input, InputNumber, Modal, Popconfirm, Progress, Row, Select,
  Space, Spin, Table, Tag, Tooltip, Typography,
} from "antd";
import {
  ArrowLeftOutlined, PlusOutlined, CheckCircleOutlined,
  StopOutlined, DeleteOutlined, EditOutlined, DollarOutlined,
} from "@ant-design/icons";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  ResponsiveContainer, Legend,
} from "recharts";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { TextArea } = Input;
const API_URL = process.env.REACT_APP_API_URL;

// ── Colores por tipo de actividad ──
const TIPO_COLOR = {
  "Preparación del terreno": "#8B4513",
  Siembra:       "green",
  Fertilización: "blue",
  Riego:         "cyan",
  Poda:          "orange",
  ControlPlagas: "red",
  Fumigación:    "volcano",
  Cosecha:       "gold",
  Aporque:       "lime",
  Otro:          "default",
};

const TIPOS_ACTIVIDAD = [
  "Preparación del terreno",
  "Siembra","Fertilización","Riego","Poda","ControlPlagas",
  "Fumigación","Cosecha","Aporque","Otro",
];

const ESTADO_COLOR = {
  Sembrado: "blue", EnCrecimiento: "green", EnFloracion: "purple",
  ListoCosechar: "gold", Cosechado: "default", Perdido: "red",
};
const ESTADO_LABEL = {
  Sembrado: "Sembrado", EnCrecimiento: "En crecimiento", EnFloracion: "En floración",
  ListoCosechar: "Listo p/cosechar", Cosechado: "Cosechado", Perdido: "Perdido",
};
const ESTADOS_CULTIVO = Object.keys(ESTADO_LABEL);

const toArr = v => Array.isArray(v?.$values) ? v.$values : Array.isArray(v) ? v : [];

export default function FrmCultivoDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [cultivo, setCultivo] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [alerta, setAlerta] = useState(null);
  const [productos, setProductos] = useState([]);

  // Modal: nueva actividad
  const [modalActividad, setModalActividad] = useState(false);
  const [formActividad] = Form.useForm();
  const [insumosActividad, setInsumosActividad] = useState([]);
  const [guardandoActividad, setGuardandoActividad] = useState(false);

  // Modal: completar actividad
  const [modalCompletar, setModalCompletar] = useState(false);
  const [actividadCompletar, setActividadCompletar] = useState(null);
  const [formCompletar] = Form.useForm();
  const [insumosCompletar, setInsumosCompletar] = useState([]);
  const [guardandoCompletar, setGuardandoCompletar] = useState(false);

  const cargar = useCallback(async () => {
    try {
      setCargando(true);
      const resp = await axios.get(`${API_URL}/Cultivo/${id}`);
      const d = resp.data;
      setCultivo({
        ...d,
        actividades: toArr(d.actividades).map(a => ({
          ...a,
          insumos: toArr(a.insumos),
        })),
      });
    } catch {
      setAlerta({ tipo: "error", texto: "No se pudo cargar el cultivo." });
    } finally {
      setCargando(false);
    }
  }, [id]);

  useEffect(() => {
    cargar();
    // Cargar productos para selección de insumos
    axios.get(`${API_URL}/Producto`).then(r => {
      const lista = Array.isArray(r.data?.$values) ? r.data.$values
        : Array.isArray(r.data) ? r.data : [];
      setProductos(lista);
    }).catch(() => {});
  }, [cargar]);

  // ── Cambiar estado del cultivo ──
  const cambiarEstado = async (nuevoEstado) => {
    try {
      await axios.patch(`${API_URL}/Cultivo/${id}/estado`, JSON.stringify(nuevoEstado), {
        headers: { "Content-Type": "application/json" },
      });
      await cargar();
      setAlerta({ tipo: "success", texto: `Estado cambiado a "${ESTADO_LABEL[nuevoEstado]}".` });
    } catch {
      setAlerta({ tipo: "error", texto: "Error al cambiar estado." });
    }
  };

  // ── Cancelar actividad ──
  const cancelarActividad = async (actId) => {
    try {
      await axios.patch(`${API_URL}/Cultivo/actividad/${actId}/cancelar`);
      await cargar();
    } catch {
      setAlerta({ tipo: "error", texto: "Error al cancelar la actividad." });
    }
  };

  // ── Eliminar actividad ──
  const eliminarActividad = async (actId) => {
    try {
      await axios.delete(`${API_URL}/Cultivo/actividad/${actId}`);
      await cargar();
    } catch {
      setAlerta({ tipo: "error", texto: "Error al eliminar la actividad." });
    }
  };

  // ── Guardar nueva actividad ──
  const guardarActividad = async () => {
    try {
      const values = await formActividad.validateFields();
      setGuardandoActividad(true);
      const body = {
        cultivoId: Number(id),
        tipoActividad: values.tipoActividad,
        descripcion: values.descripcion ?? null,
        fechaPlanificada: values.fechaPlanificada.format("YYYY-MM-DD"),
        costoManoObra: values.costoManoObra ?? 0,
        observaciones: null,
        insumos: insumosActividad.filter(i => i.productoId).map(i => ({
          productoId: i.productoId,
          cantidadPlanificada: i.cantidad ?? 0,
          costoUnitario: i.costoUnitario ?? 0,
        })),
      };
      await axios.post(`${API_URL}/Cultivo/actividad`, body);
      setModalActividad(false);
      formActividad.resetFields();
      setInsumosActividad([]);
      await cargar();
      setAlerta({ tipo: "success", texto: "✅ Actividad creada correctamente." });
    } catch (err) {
      if (err?.errorFields) return;
      setAlerta({ tipo: "error", texto: "Error al crear la actividad." });
    } finally {
      setGuardandoActividad(false);
    }
  };

  // ── Abrir modal de completar ──
  const abrirCompletar = (actividad) => {
    setActividadCompletar(actividad);
    formCompletar.setFieldsValue({
      fechaEjecutada: dayjs(),
      costoManoObra: actividad.costoManoObra ?? 0,
      observaciones: "",
    });
    // Pre-cargar insumos planificados
    const insumosInit = actividad.insumos.map(i => ({
      productoId: i.productoId,
      nombre: i.productoNombre,
      cantidad: i.cantidadPlanificada,
      costoUnitario: i.costoUnitario,
    }));
    setInsumosCompletar(insumosInit);
    setModalCompletar(true);
  };

  // ── Confirmar completar actividad ──
  const confirmarCompletar = async () => {
    try {
      const values = await formCompletar.validateFields();
      setGuardandoCompletar(true);
      const body = {
        fechaEjecutada: values.fechaEjecutada.format("YYYY-MM-DD"),
        costoManoObra: values.costoManoObra ?? 0,
        observaciones: values.observaciones ?? null,
        insumos: insumosCompletar.filter(i => i.productoId && (i.cantidad ?? 0) > 0).map(i => ({
          productoId: i.productoId,
          cantidadUsada: i.cantidad,
          costoUnitario: i.costoUnitario ?? 0,
        })),
      };
      await axios.patch(`${API_URL}/Cultivo/actividad/${actividadCompletar.id}/completar`, body);
      setModalCompletar(false);
      formCompletar.resetFields();
      setInsumosCompletar([]);
      setActividadCompletar(null);
      await cargar();
      setAlerta({ tipo: "success", texto: "✅ Actividad completada. Inventario actualizado." });
    } catch (err) {
      if (err?.errorFields) return;
      const msg = err?.response?.data?.mensaje ?? "Error al completar.";
      setAlerta({ tipo: "error", texto: `❌ ${msg}` });
    } finally {
      setGuardandoCompletar(false);
    }
  };

  // ── Helpers para filas de insumos ──
  const agregarInsumo = (setFn) => setFn(prev => [
    ...prev, { _key: Date.now(), productoId: null, nombre: "", cantidad: 0, costoUnitario: 0 },
  ]);

  const actualizarInsumo = (setFn, key, campo, valor) => {
    setFn(prev => prev.map(i => i._key === key ? { ...i, [campo]: valor } : i));
  };

  const eliminarInsumo = (setFn, key) => {
    setFn(prev => prev.filter(i => i._key !== key));
  };

  // ── Gráfico de costos por actividad ──
  const datosCosto = (cultivo?.actividades ?? [])
    .filter(a => a.estado === "Completada" && (a.costoManoObra > 0 || a.costoInsumos > 0))
    .map(a => ({
      nombre: a.tipoActividad,
      "Mano de obra": Number(a.costoManoObra ?? 0),
      Insumos: Number(a.costoInsumos ?? 0),
    }));

  if (!cultivo && !cargando) {
    return (
      <div style={{ padding: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/dashboard/cultivos")}>Volver</Button>
        <Empty description="Cultivo no encontrado." style={{ marginTop: 32 }} />
      </div>
    );
  }

  const actividades = cultivo?.actividades ?? [];
  const total = actividades.length;
  const completadas = actividades.filter(a => a.estado === "Completada").length;
  const pendientes  = actividades.filter(a => a.estado === "Pendiente").length;
  const pct = total > 0 ? Math.round((completadas / total) * 100) : 0;

  // ── Columnas de actividades ──
  const columnas = [
    {
      title: "Actividad",
      key: "tipo",
      width: 150,
      render: (_, r) => (
        <div>
          <Tag color={TIPO_COLOR[r.tipoActividad] ?? "default"}>{r.tipoActividad}</Tag>
          {r.descripcion && (
            <div><Text type="secondary" style={{ fontSize: 11 }}>{r.descripcion}</Text></div>
          )}
        </div>
      ),
    },
    {
      title: "Planificada",
      dataIndex: "fechaPlanificada",
      width: 110,
      render: v => {
        const d = dayjs(v);
        const vencida = d.isBefore(dayjs(), "day");
        return (
          <Text style={{ color: vencida ? "#ff4d4f" : undefined }}>
            {d.format("DD/MM/YYYY")}
          </Text>
        );
      },
      sorter: (a, b) => dayjs(a.fechaPlanificada).valueOf() - dayjs(b.fechaPlanificada).valueOf(),
      defaultSortOrder: "ascend",
    },
    {
      title: "Ejecutada",
      dataIndex: "fechaEjecutada",
      width: 110,
      render: v => v ? dayjs(v).format("DD/MM/YYYY") : <Text type="secondary">—</Text>,
    },
    {
      title: "Estado",
      dataIndex: "estado",
      width: 110,
      render: v => (
        <Tag color={v === "Completada" ? "green" : v === "Cancelada" ? "red" : "orange"}>
          {v}
        </Tag>
      ),
    },
    {
      title: "Costo M.O.",
      dataIndex: "costoManoObra",
      width: 110,
      align: "right",
      render: v => v > 0 ? `$${Number(v).toLocaleString("es-CO")}` : <Text type="secondary">—</Text>,
    },
    {
      title: "Costo insumos",
      dataIndex: "costoInsumos",
      width: 120,
      align: "right",
      render: v => v > 0 ? `$${Number(v).toLocaleString("es-CO")}` : <Text type="secondary">—</Text>,
    },
    {
      title: "Total",
      dataIndex: "costoTotal",
      width: 110,
      align: "right",
      render: v => v > 0
        ? <Text strong style={{ color: "#1677ff" }}>${Number(v).toLocaleString("es-CO")}</Text>
        : <Text type="secondary">—</Text>,
    },
    {
      title: "Acciones",
      key: "acciones",
      width: 130,
      align: "center",
      render: (_, r) => (
        <Space size={4}>
          {r.estado === "Pendiente" && (
            <Tooltip title="Marcar como completada">
              <Button size="small" type="primary" icon={<CheckCircleOutlined />}
                onClick={() => abrirCompletar(r)} />
            </Tooltip>
          )}
          {r.estado === "Pendiente" && (
            <Popconfirm title="¿Cancelar esta actividad?"
              onConfirm={() => cancelarActividad(r.id)}
              okText="Sí" cancelText="No">
              <Button size="small" icon={<StopOutlined />} />
            </Popconfirm>
          )}
          <Popconfirm title="¿Eliminar actividad?"
            onConfirm={() => eliminarActividad(r.id)}
            okText="Sí, eliminar" cancelText="No" okButtonProps={{ danger: true }}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Fila expandida: detalle de insumos
  const expandido = (record) => {
    if (record.insumos.length === 0)
      return <Text type="secondary" style={{ padding: "0 16px" }}>Sin insumos registrados.</Text>;
    return (
      <Table
        rowKey="id"
        size="small"
        pagination={false}
        dataSource={record.insumos}
        columns={[
          { title: "Producto", dataIndex: "productoNombre", key: "prod" },
          {
            title: "Planificado",
            dataIndex: "cantidadPlanificada",
            align: "right",
            width: 120,
            render: v => `${Number(v).toFixed(2)}`,
          },
          {
            title: "Usado",
            dataIndex: "cantidadUsada",
            align: "right",
            width: 100,
            render: v => v != null ? `${Number(v).toFixed(2)}` : <Text type="secondary">—</Text>,
          },
          {
            title: "$/unidad",
            dataIndex: "costoUnitario",
            align: "right",
            width: 110,
            render: v => `$${Number(v).toLocaleString("es-CO")}`,
          },
          {
            title: "Costo total",
            dataIndex: "costoTotal",
            align: "right",
            width: 120,
            render: v => (
              <Text strong style={{ color: "#1677ff" }}>
                ${Number(v).toLocaleString("es-CO")}
              </Text>
            ),
          },
        ]}
      />
    );
  };

  // ── Tabla de insumos reutilizable ──
  const TablaInsumos = ({ insumos, setInsumos }) => (
    <div>
      {insumos.map((ins, idx) => (
        <Row key={ins._key} gutter={8} style={{ marginBottom: 8 }} align="middle">
          <Col xs={24} sm={10}>
            <Select
              showSearch
              placeholder="Producto..."
              style={{ width: "100%" }}
              optionFilterProp="children"
              value={ins.productoId}
              onChange={val => {
                const prod = productos.find(p => p.id === val);
                actualizarInsumo(setInsumos, ins._key, "productoId", val);
                actualizarInsumo(setInsumos, ins._key, "nombre", prod?.nombre ?? "");
              }}
            >
              {productos.map(p => (
                <Select.Option key={p.id} value={p.id}>{p.nombre}</Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={12} sm={6}>
            <InputNumber
              placeholder="Cantidad"
              min={0} step={0.5} precision={3}
              style={{ width: "100%" }}
              value={ins.cantidad}
              onChange={val => actualizarInsumo(setInsumos, ins._key, "cantidad", val)}
            />
          </Col>
          <Col xs={10} sm={6}>
            <InputNumber
              placeholder="$/unidad"
              min={0} step={100}
              style={{ width: "100%" }}
              formatter={v => v ? `$ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""}
              parser={v => v.replace(/\$\s?|(,*)/g, "")}
              value={ins.costoUnitario}
              onChange={val => actualizarInsumo(setInsumos, ins._key, "costoUnitario", val)}
            />
          </Col>
          <Col xs={2} style={{ textAlign: "center" }}>
            <Button size="small" danger
              onClick={() => eliminarInsumo(setInsumos, ins._key)}>✕</Button>
          </Col>
        </Row>
      ))}
      <Button size="small" type="dashed" onClick={() => agregarInsumo(setInsumos)}>
        + Agregar insumo
      </Button>
    </div>
  );

  return (
    <div style={{ padding: 16 }}>
      {/* ── Encabezado ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
        <Space wrap>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/dashboard/cultivos")}>
            Volver
          </Button>
          <Title level={3} style={{ margin: 0 }}>
            🌿 {cultivo?.nombre ?? "Cargando..."}
          </Title>
          {cultivo && (
            <Tag color={ESTADO_COLOR[cultivo.estado] ?? "default"} style={{ fontSize: 13 }}>
              {ESTADO_LABEL[cultivo.estado] ?? cultivo.estado}
            </Tag>
          )}
        </Space>
        <Space wrap>
          <Select
            placeholder="Cambiar estado..."
            style={{ width: 180 }}
            onChange={cambiarEstado}
            value={undefined}
          >
            {ESTADOS_CULTIVO.map(e => (
              <Select.Option key={e} value={e}>{ESTADO_LABEL[e]}</Select.Option>
            ))}
          </Select>
          <Button icon={<EditOutlined />}
            onClick={() => navigate(`/dashboard/cultivos/${id}/editar`)}>
            Editar
          </Button>
        </Space>
      </div>

      {alerta && (
        <Alert type={alerta.tipo} message={alerta.texto} showIcon closable
          onClose={() => setAlerta(null)} style={{ marginBottom: 12 }} />
      )}

      <Spin spinning={cargando}>
        {cultivo && (
          <>
            {/* ── Info general ── */}
            <Card size="small" style={{ marginBottom: 12 }}>
              <Descriptions size="small" column={{ xs: 1, sm: 2, md: 4 }}>
                {cultivo.tipoCultivo && (
                  <Descriptions.Item label="Tipo">{cultivo.tipoCultivo}</Descriptions.Item>
                )}
                {cultivo.lote && (
                  <Descriptions.Item label="Lote">📍 {cultivo.lote}</Descriptions.Item>
                )}
                {cultivo.areaHectareas && (
                  <Descriptions.Item label="Área">{cultivo.areaHectareas} ha</Descriptions.Item>
                )}
                <Descriptions.Item label="Siembra">
                  {dayjs(cultivo.fechaSiembra).format("DD/MM/YYYY")}
                </Descriptions.Item>
                {cultivo.fechaEstimadaCosecha && (
                  <Descriptions.Item label="Est. cosecha">
                    {dayjs(cultivo.fechaEstimadaCosecha).format("DD/MM/YYYY")}
                  </Descriptions.Item>
                )}
                {cultivo.productoSemilla && (
                  <Descriptions.Item label="Semilla">
                    {cultivo.productoSemilla}
                    {cultivo.cantidadSemilla && ` (${cultivo.cantidadSemilla})`}
                  </Descriptions.Item>
                )}
              </Descriptions>
              {cultivo.observaciones && (
                <Text type="secondary" style={{ fontSize: 12 }}>📝 {cultivo.observaciones}</Text>
              )}
            </Card>

            {/* ── Tarjetas resumen ── */}
            <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
              <Col xs={12} md={6}>
                <Card size="small">
                  <Text type="secondary">Actividades</Text>
                  <div style={{ fontSize: 24, fontWeight: 700 }}>{total}</div>
                  <Progress percent={pct} size="small" strokeColor="#1677ff"
                    format={() => `${completadas}/${total}`} />
                </Card>
              </Col>
              <Col xs={12} md={6}>
                <Card size="small">
                  <Text type="secondary">Pendientes</Text>
                  <div style={{ fontSize: 24, fontWeight: 700,
                    color: pendientes > 0 ? "#fa8c16" : "#8c8c8c" }}>
                    {pendientes}
                  </div>
                </Card>
              </Col>
              <Col xs={12} md={6}>
                <Card size="small">
                  <Text type="secondary">Costo mano de obra</Text>
                  <div style={{ fontSize: 20, fontWeight: 700, color: "#595959" }}>
                    ${Number(cultivo.costoManoObraTotal ?? 0).toLocaleString("es-CO")}
                  </div>
                </Card>
              </Col>
              <Col xs={12} md={6}>
                <Card size="small" style={{ borderColor: "#1677ff" }}>
                  <Text type="secondary">Costo total real</Text>
                  <div style={{ fontSize: 20, fontWeight: 700, color: "#1677ff" }}>
                    ${Number(cultivo.costoTotal ?? 0).toLocaleString("es-CO")}
                  </div>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    Insumos: ${Number(cultivo.costoInsumosTotal ?? 0).toLocaleString("es-CO")}
                  </Text>
                </Card>
              </Col>
            </Row>

            {/* ── Gráfico de costos ── */}
            {datosCosto.length > 0 && (
              <Card title="💰 Distribución de costos por actividad" style={{ marginBottom: 12 }}>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={datosCosto} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="nombre" />
                    <YAxis />
                    <RTooltip formatter={v => `$${Number(v).toLocaleString("es-CO")}`} />
                    <Legend />
                    <Bar dataKey="Mano de obra" fill="#595959" radius={[4,4,0,0]} />
                    <Bar dataKey="Insumos" fill="#1677ff" radius={[4,4,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            )}

            {/* ── Tabla de actividades ── */}
            <Card
              title="📋 Actividades del cultivo"
              extra={
                <Button type="primary" size="small" icon={<PlusOutlined />}
                  onClick={() => {
                    formActividad.resetFields();
                    setInsumosActividad([]);
                    setModalActividad(true);
                  }}>
                  Nueva actividad
                </Button>
              }
            >
              {actividades.length === 0 ? (
                <Empty description="Sin actividades programadas. ¡Agrega la primera!" />
              ) : (
                <Table
                  rowKey="id"
                  columns={columnas}
                  dataSource={actividades}
                  expandable={{ expandedRowRender: expandido }}
                  pagination={{ pageSize: 20, showSizeChanger: true }}
                  scroll={{ x: "max-content" }}
                  size="small"
                  rowClassName={r =>
                    r.estado === "Pendiente" && dayjs(r.fechaPlanificada).isBefore(dayjs(), "day")
                      ? "row-vencida" : ""}
                />
              )}
            </Card>
          </>
        )}
      </Spin>

      {/* ══ Modal: nueva actividad ══ */}
      <Modal
        title="➕ Nueva actividad"
        open={modalActividad}
        onCancel={() => setModalActividad(false)}
        onOk={guardarActividad}
        okText="Guardar actividad"
        confirmLoading={guardandoActividad}
        width={700}
        destroyOnClose
      >
        <Form form={formActividad} layout="vertical">
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item label="Tipo de actividad" name="tipoActividad"
                rules={[{ required: true, message: "Seleccione el tipo." }]}>
                <Select placeholder="Seleccione...">
                  {TIPOS_ACTIVIDAD.map(t => (
                    <Select.Option key={t} value={t}>
                      <Tag color={TIPO_COLOR[t] ?? "default"}>{t}</Tag>
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Fecha planificada" name="fechaPlanificada"
                rules={[{ required: true, message: "Ingrese la fecha." }]}>
                <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Descripción" name="descripcion">
            <Input placeholder="Ej: Primera fertilización NPK 15-15-15..." />
          </Form.Item>
          <Form.Item label="Costo mano de obra ($)" name="costoManoObra" initialValue={0}>
            <InputNumber min={0} step={5000} style={{ width: "100%" }}
              formatter={v => v ? `$ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""}
              parser={v => v.replace(/\$\s?|(,*)/g, "")} />
          </Form.Item>

          <div style={{ marginBottom: 8 }}>
            <Text strong>Insumos planificados (opcional)</Text>
            <Text type="secondary" style={{ fontSize: 12, display: "block" }}>
              Se pueden agregar al completar la actividad también.
            </Text>
          </div>
          <TablaInsumos insumos={insumosActividad} setInsumos={setInsumosActividad} />
        </Form>
      </Modal>

      {/* ══ Modal: completar actividad ══ */}
      <Modal
        title={`✅ Completar: ${actividadCompletar?.tipoActividad ?? ""}`}
        open={modalCompletar}
        onCancel={() => setModalCompletar(false)}
        onOk={confirmarCompletar}
        okText="Confirmar y descontar inventario"
        okButtonProps={{ type: "primary", icon: <CheckCircleOutlined /> }}
        confirmLoading={guardandoCompletar}
        width={720}
        destroyOnClose
      >
        <Alert
          type="info"
          message="Los insumos que registres aquí serán descontados del inventario automáticamente."
          showIcon
          style={{ marginBottom: 12 }}
        />
        <Form form={formCompletar} layout="vertical">
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item label="Fecha de ejecución" name="fechaEjecutada"
                rules={[{ required: true, message: "Ingrese la fecha." }]}>
                <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Costo mano de obra ($)" name="costoManoObra">
                <InputNumber min={0} step={5000} style={{ width: "100%" }}
                  formatter={v => v ? `$ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""}
                  parser={v => v.replace(/\$\s?|(,*)/g, "")} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Observaciones" name="observaciones">
            <TextArea rows={2} maxLength={500} showCount
              placeholder="Notas sobre la ejecución, condiciones, resultados..." />
          </Form.Item>

          <div style={{ marginBottom: 8 }}>
            <Text strong>
              <DollarOutlined style={{ marginRight: 4 }} />
              Insumos utilizados (se descontarán del inventario)
            </Text>
          </div>
          <TablaInsumos insumos={insumosCompletar} setInsumos={setInsumosCompletar} />

          {insumosCompletar.length > 0 && (
            <div style={{ marginTop: 8, textAlign: "right" }}>
              <Text strong>
                Costo insumos: $
                {insumosCompletar
                  .reduce((s, i) => s + (i.cantidad ?? 0) * (i.costoUnitario ?? 0), 0)
                  .toLocaleString("es-CO")}
              </Text>
            </div>
          )}
        </Form>
      </Modal>

      <style>{`
        .row-vencida td { background: #fff2f0 !important; }
      `}</style>
    </div>
  );
}
